import React, { useEffect, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { CU2_TOKENS } from '../tokens';
import { cn } from '../lib/utils';
import { ArrowUp, ArrowDown } from 'lucide-react';

// ---- Local types (mirror of API response shape) ----

interface AucDataPoint {
  scoreType: string;
  windowDays: number;
  auc: number;
  precision: number;
  recall: number;
  sampleSize: number;
  computedAt: string;
}

interface ChampionChallengerStatus {
  enabled: boolean;
  activeModel: 'aire-ml' | 'claude-baseline';
  checkedAt: string;
}

interface ModelHealthResponse {
  aucSeries: AucDataPoint[];
  accuracyMetrics: AucDataPoint[];
  championChallenger: ChampionChallengerStatus;
  generatedAt: string;
}

// ---- Sort helpers ----

type SortKey = keyof AucDataPoint;
type SortDir = 'asc' | 'desc';

function sortedMetrics(rows: AucDataPoint[], key: SortKey, dir: SortDir): AucDataPoint[] {
  return [...rows].sort((a, b) => {
    const av = a[key];
    const bv = b[key];
    if (av === bv) return 0;
    const cmp = av < bv ? -1 : 1;
    return dir === 'asc' ? cmp : -cmp;
  });
}

// ---- Loading skeleton ----

function LoadingSkeleton(): React.ReactElement {
  return (
    <div className="space-y-4">
      <div className="animate-pulse bg-sunken rounded-xl h-24" />
      <div className="animate-pulse bg-sunken rounded-xl h-64" />
      <div className="animate-pulse bg-sunken rounded-xl h-48" />
    </div>
  );
}

// ---- Champion/Challenger Status Card ----

function ChampionChallengerCard({
  cc,
}: {
  cc: ChampionChallengerStatus;
}): React.ReactElement {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-sunken p-6">
      <p className="text-base font-semibold text-ink font-heading">Model Status</p>
      <div className="flex items-center gap-3 mt-3">
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-brand-tint text-brand-dark">
          Active: {cc.activeModel}
        </span>
        <span
          className={cn(
            'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium',
            cc.enabled ? 'bg-pass-soft text-pass' : 'bg-sunken text-ink-soft'
          )}
        >
          Champion/Challenger: {cc.enabled ? 'Enabled' : 'Disabled'}
        </span>
      </div>
      <p className="text-xs text-ink-muted mt-3">
        As of {new Date(cc.checkedAt).toLocaleString()}
      </p>
    </div>
  );
}

// ---- AUC Trend Chart ----

function AucChart({ series }: { series: AucDataPoint[] }): React.ReactElement {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-sunken p-6">
      <p className="text-base font-semibold text-ink mb-4 font-heading">AUC by Score Type</p>
      {series.length === 0 ? (
        <div className="h-64 flex items-center justify-center">
          <p className="text-sm text-ink-muted">No model runs recorded yet.</p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={series}>
            <CartesianGrid strokeDasharray="3 3" stroke={CU2_TOKENS.gray} />
            <XAxis dataKey="scoreType" tick={{ fontSize: 12 }} />
            <YAxis
              domain={[0, 1]}
              tick={{ fontSize: 12 }}
              tickFormatter={(v: number) => v.toFixed(2)}
            />
            <Tooltip formatter={(value: number) => [value.toFixed(3), 'AUC']} />
            <Legend />
            <Line
              type="monotone"
              dataKey="auc"
              stroke={CU2_TOKENS.blue}
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

// ---- Accuracy Metrics Table ----

const COLUMNS: { key: SortKey; label: string }[] = [
  { key: 'scoreType', label: 'Score Type' },
  { key: 'auc', label: 'AUC' },
  { key: 'precision', label: 'Precision' },
  { key: 'recall', label: 'Recall' },
  { key: 'sampleSize', label: 'Sample Size' },
];

function AccuracyTable({ metrics }: { metrics: AucDataPoint[] }): React.ReactElement {
  const [sortKey, setSortKey] = useState<SortKey>('scoreType');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  function handleSort(key: SortKey): void {
    if (key === sortKey) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  const sorted = sortedMetrics(metrics, sortKey, sortDir);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-sunken p-6">
      <div className="flex items-baseline gap-2 mb-4">
        <p className="text-base font-semibold text-ink font-heading">Accuracy Metrics</p>
        <span className="text-xs text-ink-muted">Sortable by any column</span>
      </div>
      <div className="border border-line rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-bg">
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  className="px-4 py-3 text-left text-xs font-medium text-ink-muted uppercase tracking-wider"
                >
                  <button
                    onClick={() => handleSort(col.key)}
                    className="flex items-center gap-1 hover:text-ink transition-colors"
                  >
                    {col.label}
                    {sortKey === col.key && (
                      <span className="text-running">
                        {sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
                      </span>
                    )}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-8 text-center text-sm text-ink-muted"
                >
                  No accuracy data available.
                </td>
              </tr>
            ) : (
              sorted.map((row, idx) => (
                <tr key={idx} className="border-t border-sunken hover:bg-brand/5">
                  <td className="px-4 py-3 text-ink">{row.scoreType}</td>
                  <td className="px-4 py-3 text-ink-soft">{row.auc.toFixed(3)}</td>
                  <td className="px-4 py-3 text-ink-soft">{row.precision.toFixed(3)}</td>
                  <td className="px-4 py-3 text-ink-soft">{row.recall.toFixed(3)}</td>
                  <td className="px-4 py-3 text-ink-soft">{row.sampleSize}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---- ModelHealthTab ----

export function ModelHealthTab(): React.ReactElement {
  const [data, setData] = useState<ModelHealthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchData(): Promise<void> {
      try {
        const token = localStorage.getItem('crm_dev_token') ?? '';
        const response = await fetch('/api/settings/model-health', {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to load model health data: ${response.status}`);
        }

        const payload = (await response.json()) as ModelHealthResponse;
        if (!cancelled) setData(payload);
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Unknown error';
          setError(message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void fetchData();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return <LoadingSkeleton />;
  }

  if (error !== null) {
    return (
      <div className="rounded-lg border border-fail-soft bg-fail-soft p-6 text-sm text-fail">
        Failed to load model health data. Please refresh.
      </div>
    );
  }

  if (data === null) {
    return <div />;
  }

  return (
    <div className="space-y-4">
      <ChampionChallengerCard cc={data.championChallenger} />
      <AucChart series={data.aucSeries} />
      <AccuracyTable metrics={data.accuracyMetrics} />
    </div>
  );
}
